import { useEffect, useRef } from 'react';
import L from 'leaflet';
import type { Merchant, MerchantStatus } from '@/lib/merchants';
import { getMerchantStatus } from '@/lib/merchants';

interface MerchantMapProps {
  merchants: Merchant[];
  onSelectMerchant: (merchant: Merchant) => void;
  selectedMerchant: Merchant | null;
  className?: string;
}

/** Map status → marker fill colour */
const STATUS_COLORS: Record<MerchantStatus, string> = {
  active: '#22c55e',   // green-500  — Active BCH Merchant
  merchant: '#eab308', // yellow-500 — BCH Merchant
  new: '#d1d5db',      // gray-300   — Newly Listed
};

// Custom marker SVG — colour driven by merchant status
function createMarkerIcon(status: MerchantStatus, verified: boolean): L.DivIcon {
  const color = STATUS_COLORS[status];
  const pulseCss = status === 'active'
    ? 'animation: bch-pulse 2s ease-in-out infinite;'
    : '';

  // Verified merchants get a bold BCH-green ring around the pin plus a small
  // check badge, so they visibly stand out from unverified listings.
  const verifiedRing = verified
    ? 'border: 3px solid #0AC18E; box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 3px rgba(10,193,142,0.35);'
    : 'border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);';

  const checkBadge = verified
    ? `<div style="
          position: absolute; top: -4px; right: -4px;
          width: 15px; height: 15px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          background: #0AC18E;
          border: 2px solid white;
          box-shadow: 0 1px 3px rgba(0,0,0,0.35);
        ">
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>`
    : '';

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="position: relative; width: 32px; height: 32px;">
        <div style="
          width: 32px; height: 32px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          background: ${color};
          ${verifiedRing}
          cursor: pointer;
          ${pulseCss}
        ">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        ${checkBadge}
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}

function createSelectedMarkerIcon(): L.DivIcon {
  return L.divIcon({
    className: 'custom-marker-selected',
    html: `
      <div style="
        width: 40px; height: 40px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 50%;
        background: #0AC18E;
        border: 3px solid white;
        box-shadow: 0 2px 12px rgba(10,193,142,0.5), 0 0 0 4px rgba(10,193,142,0.2);
        cursor: pointer;
        transform: scale(1.1);
        transition: transform 0.2s;
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
}

export function MerchantMap({
  merchants,
  onSelectMerchant,
  selectedMerchant,
  className = '',
}: MerchantMapProps) {
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [20, 0],
      zoom: 2,
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    // Fix map size on next tick (Leaflet needs the container fully rendered)
    const timer = setTimeout(() => {
      if (mapRef.current) {
        map.invalidateSize();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      map.remove();
      mapRef.current = null;
      markersRef.current = null;
    };
  }, []);

  // Update markers when merchants change
  useEffect(() => {
    if (!markersRef.current || !mapRef.current) return;

    markersRef.current.clearLayers();

    const bounds: L.LatLngExpression[] = [];

    for (const merchant of merchants) {
      if (!merchant.location) continue;

      const { lat, lng } = merchant.location;
      const isSelected = selectedMerchant?.pubkey === merchant.pubkey;
      const status = getMerchantStatus(merchant);

      const icon = isSelected ? createSelectedMarkerIcon() : createMarkerIcon(status, merchant.verified);

      const marker = L.marker([lat, lng], { icon });

      // No popup — details are shown in the side sheet / detail card below the map
      marker.on('click', () => {
        onSelectMerchant(merchant);
      });

      markersRef.current!.addLayer(marker);
      bounds.push([lat, lng]);
    }

    // Fit to bounds if we have markers
    if (bounds.length > 0 && !selectedMerchant) {
      const latLngBounds = L.latLngBounds(bounds);
      mapRef.current.fitBounds(latLngBounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [merchants, selectedMerchant, onSelectMerchant]);

  // Pan to selected merchant
  useEffect(() => {
    if (!mapRef.current || !selectedMerchant?.location) return;

    mapRef.current.setView(
      [selectedMerchant.location.lat, selectedMerchant.location.lng],
      Math.max(mapRef.current.getZoom(), 10),
      { animate: true },
    );
  }, [selectedMerchant]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      mapRef.current?.invalidateSize();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative z-0 w-full rounded-xl overflow-hidden border border-border ${className}`}
      style={{ minHeight: '400px' }}
    />
  );
}
